import HttpRequest from "@/util/HttpRequest";
import { mergePostContents } from "@/util/DataUtil";

const promiseCache = new Map();

function getCacheKey(actionName, params) {
  return `${actionName}:${JSON.stringify(params)}`;
}

function cachedApiCall(cacheKey, apiCallFn, commit, mutationName, cacheTime = 5000) {
  // console.log('캐시 키:', cacheKey); // 디버깅을 위한 캐시 키 출력

  if (!promiseCache.has(cacheKey)) {
    // console.log('캐시 미스: 새 요청 실행');
    const promise = apiCallFn().finally(() => {
      setTimeout(() => {
        promiseCache.delete(cacheKey);
        // console.log('캐시 삭제:', cacheKey);
      }, cacheTime);
    });
    promiseCache.set(cacheKey, promise);
  }

  // 캐시에서 데이터를 가져와서 commit 호출
  return promiseCache.get(cacheKey).then((response) => {
    if (commit && mutationName && response) {
      commit(mutationName, response);
    }
    return response;
  });
}

// 조건을 파라미터로 변환하는 재귀 함수
function processCondition(condition, prefix, option) {
  const subConditions = condition.conditions;
  const key = condition.key && condition.key.trim();

  if (key) {
    const operator = condition.operator && `${condition.operator}`.trim();
    const value = condition.value;

    // 배열인 경우 먼저 필터링
    if (Array.isArray(value)) {
      const filteredValues = value.filter((v) => v !== undefined && v !== null && `${v}`.trim() !== "");
      if (filteredValues.length === 0) return false;

      if (operator) {
        option.params[`${prefix}[key]`] = key;
        option.params[`${prefix}[operator]`] = operator;
        filteredValues.forEach((v, vIndex) => {
          option.params[`${prefix}[value][${vIndex}]`] = `${v}`.trim();
        });
        if (condition.boolean) {
          option.params[`${prefix}[boolean]`] = condition.boolean;
        }
        return true;
      }
      return false;
    }

    // 배열이 아닌 경우
    if (operator) {
      const opUpper = `${operator}`.toUpperCase();

      // '!=' / '<>' 는 빈 문자열('')도 의미 있는 필터다("빈 값이 아님").
      // 그 외 연산자(=, >, <, LIKE 등)에서 빈 값은 "검색 조건 미입력"으로 보고 제외한다.
      //  - 예: 사업장 미선택 시 field_id = '' 가 전송되어 결과가 0건이 되는 문제 방지
      //  - 빈 값 "매칭"이 필요한 화면은 REGEXP "^$" 처럼 비어있지 않은 값을 가진 연산자로 표현한다.
      const allowEmptyValue = opUpper === "!=" || opUpper === "<>";

      // undefined/null은 "값 미입력"으로 간주하여 항상 제외한다.
      if (value !== undefined && value !== null) {
        const trimmed = `${value}`.trim();
        if (trimmed !== "" || allowEmptyValue) {
          option.params[`${prefix}[key]`] = key;
          option.params[`${prefix}[operator]`] = operator;
          option.params[`${prefix}[value]`] = trimmed;
          if (condition.boolean) {
            option.params[`${prefix}[boolean]`] = condition.boolean;
          }
          return true;
        }
      }
    }
    return false;
  } else if (subConditions && Array.isArray(subConditions)) {
    const boolean = condition.boolean;

    // 먼저 유효한 하위 조건이 있는지 확인
    const tempOption = { params: {} };
    let subIndex = 0;

    for (let i = 0; i < subConditions.length; i++) {
      const subCondition = subConditions[i];
      const subPrefix = `${prefix}[conditions][${subIndex}]`;

      if (processCondition(subCondition, subPrefix, tempOption)) {
        subIndex++;
      }
    }

    // 유효한 조건이 있을 때만 추가
    if (subIndex > 0 && boolean) {
      option.params[`${prefix}[boolean]`] = boolean;
      Object.assign(option.params, tempOption.params);
      return true;
    }
    return false;
  }
  return false;
}

/**
 * 공통 요청 옵션 생성 함수
 * @param {Object} params API 요청에 필요한 파라미터
 * @param {Object} additionalParams 추가 파라미터 (옵션)
 * @returns {Object} 요청 옵션 객체
 */
function createRequestOption(params, additionalParams = {}) {
  const option = {
    params: {
      page: params.page,
      perPage: params.perPage,
      board: params.boardSlug,
      projectId: params.projectId,
      projectOrder: params.projectOrder,
      orderTarget: params.orderTarget,
      orderDirection: params.orderDirection,
      dateTarget: params.dateTarget,
      dateBegin: params.dateBegin,
      dateEnd: params.dateEnd,
      keyword: params.keyword,
      dct: params.documentCompanyType,
      hiddenFilter: params.hiddenFilter, // 숨김 필터
      ...additionalParams,
    },
  };

  // orders 처리
  if (params.orders && params.orders.length > 0) {
    let index = 0;
    for (let i = 0, order; (order = params.orders[i]); i++) {
      const target = order.target && order.target.trim();
      const direction = order.direction && `${order.direction}`.trim();
      if (target && direction) {
        option.params[`orders[${index}][target]`] = target;
        option.params[`orders[${index}][direction]`] = direction;
        index++;
      }
    }
  }

  // conditions 처리
  if (params.conditions && params.conditions.length > 0) {
    let index = 0;
    for (let i = 0; i < params.conditions.length; i++) {
      const condition = params.conditions[i];
      const prefix = `conditions[${index}]`;

      if (processCondition(condition, prefix, option)) {
        index++;
      }
    }
  }

  return option;
}

export default {
  // 결재 요청함 불러오기
  async LOAD_REQUEST_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_REQUEST_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/request", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_REQUEST_LIST"
    );
  },

  // 결재 대기함 불러오기
  async LOAD_AWAIT_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_AWAIT_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/todo", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_AWAIT_LIST"
    );
  },

  async LOAD_COMPLETE_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_COMPLETE_LIST", params);
    console.log("cacheKey", cacheKey);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/complete", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_COMPLETE_LIST"
    );
  },

  // 결재 진행중인 전체 문서 목록을 조회한다
  async LOAD_PROCESS_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_PROCESS_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/ongoing", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_PROCESS_LIST"
    );
  },

  // 결재 진행중이지만 아직 사용자가 결재하지 않은 문서 목록을 조회한다
  async LOAD_PROCESS_AWAIT_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_PROCESS_AWAIT_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/ongoing-await", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_PROCESS_AWAIT_LIST"
    );
  },

  // 결재 진행중이고 사용자가 결재한 문서 목록을 조회한다
  async LOAD_PROCESS_APPROVED_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_PROCESS_APPROVED_LIST", params);
    console.log("cacheKey", cacheKey);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/ongoing-approved", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_PROCESS_APPROVED_LIST"
    );
  },

  async LOAD_REJECT_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_REJECT_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/reject", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_REJECT_LIST"
    );
  },

  async LOAD_REFERENCE_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_REFERENCE_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params);

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/reference", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_REFERENCE_LIST"
    );
  },

  async LOAD_APPROVAL_TURN_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_APPROVAL_TURN_LIST", params);
    const forceRefresh = params.forceRefresh === true;

    // boardSlug 변경 감지를 위한 이전 요청과 비교
    const previousBoardSlug = promiseCache.get("last_boardSlug");
    const currentBoardSlug = params.boardSlug || "";
    const boardSlugChanged = previousBoardSlug !== currentBoardSlug;

    // boardSlug가 변경되었거나 forceRefresh가 true인 경우 캐시 무시
    if (boardSlugChanged || forceRefresh) {
      // console.log('boardSlug 변경되어 캐시 무시:', previousBoardSlug, '->', currentBoardSlug);
      promiseCache.delete(cacheKey);
      promiseCache.set("last_boardSlug", currentBoardSlug);
    }

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params, {
          statusIds: params.statusIds,
          ongoingType: params.ongoingType,
        });

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/approval-turn", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_APPROVAL_TURN_LIST",
      2000
    ); // 캐시 유지 시간을 2초로 줄임
  },

  async LOAD_POST_ALL({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_POST_ALL", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params, {
          preview: params.preview,
          periodTargetBegin: params.periodTargetBegin,
          periodTargetEnd: params.periodTargetEnd,
          periodDateBegin: params.periodDateBegin,
          periodDateEnd: params.periodDateEnd,
          isParent: params.isParent,
          companyIds: params.companyIds,
          statusIds: params.statusIds,
          withSlugs: params.withSlugs,
          withMeta: params.withMeta,
          metaKeys: params.metaKeys,
          onlyMeta: params.onlyMeta,
          withoutRelation: params.withoutRelation, // 작성자, 프로젝트, 게시판, 사업장 등 정보를 제외할 경우 전달
          orderTarget: params.orderTarget,
          orderDirection: params.orderDirection,
          isLeaf: params.isLeaf, // 가장 마지막 자식 문서들만 조회하는 경우 true
        });

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/all", option)
            .then((response) => {
              const data = response.data;
              if (data && data.length > 0) {
                data.forEach((post) => {
                  try {
                    post.postContents = mergePostContents(post.post);
                    delete post.post.post_contents;
                  } catch (ignore) {
                    // ignore
                  }
                });
              }
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_POST_ALL"
    );
  },

  async LOAD_POST_PENDING_OR_APPROVED({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_POST_PENDING_OR_APPROVED", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = createRequestOption(params, {
          preview: params.preview,
          periodTargetBegin: params.periodTargetBegin,
          periodTargetEnd: params.periodTargetEnd,
          periodDateBegin: params.periodDateBegin,
          periodDateEnd: params.periodDateEnd,
          isParent: params.isParent,
          companyIds: params.companyIds,
          statusIds: params.statusIds,
          withSlugs: params.withSlugs,
          withMeta: params.withMeta,
          metaKeys: params.metaKeys,
          onlyMeta: params.onlyMeta,
          withoutRelation: params.withoutRelation, // 작성자, 프로젝트, 게시판, 사업장 등 정보를 제외할 경우 전달
          orderTarget: params.orderTarget,
          orderDirection: params.orderDirection,
          isLeaf: params.isLeaf, // 가장 마지막 자식 문서들만 조회하는 경우 true
        });

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/approval/pending-or-approved", option)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_POST_ALL"
    );
  },

  async LOAD_POST_COMPLETE({ commit }, params) {
    const option = {
      params: {
        board: params.boardSlug,
        page: params.page,
        perPage: params.perPage,
        projectId: params.projectId,
        projectOrder: params.projectOrder,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get("/api/v1/approval/complete", option)
        .then((response) => {
          commit("LOAD_POST_COMPLETE", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async APPROVE_OK({ commit }, params) {
    const option = {
      content: params.content,
      uploads: params.uploads,
    };
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/approval/${postId}/approve`, option)
        .then((response) => {
          commit("APPROVE_OK", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async APPROVE_NO({ commit }, params) {
    const option = {
      comment: params.comment,
    };
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/approval/${postId}/reject`, option)
        .then((response) => {
          commit("APPROVE_NO", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 처리부서의 팀장 정보를 조회한다
   */
  async GET_VIRTUAL_COMPANY_CHIEF(_, params) {
    const virtualCompanyCode = params.code || null;
    if (!virtualCompanyCode) return null;

    return new Promise((resolve, reject) => {
      HttpRequest.get(`/api/v1/approval/chief/${virtualCompanyCode}`, {}, true)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async LOAD_POST({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;

    const cacheKey = getCacheKey("LOAD_POST", { boardSlug, postId });

    return cachedApiCall(
      cacheKey,
      () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/approval/document/${boardSlug}/${postId}`)
            .then((response) => {
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_POST",
      2000
    );
  },

  // eslint-disable-next-line no-unused-vars
  async CREATE_POST({ commit }, params) {
    const option = {
      title: params.title, // 게시글 제목
      content: params.content, // 게시글 내용
      parentId: params.parentId, // 부모 게시글 ID
      approvalLine: params.approvalLine, // 결재선 정의
      documentCompanyCodes: params.documentCompanyCodes, // 처리부서 정보
      security: params.security, // 문서보안 등급 ['일반', 대외비 1급, 대외비 2급, 인비, 극비]
      priority: params.priority, // 문서 중요도 [매우낮음, 낮음, 보통, 높음, 매우 높음]
      uploads: params.uploads,
      isTemp: params.isTemp, // 0: 바로상신, 1: 임시저장
      projectId: params.projectId,
      projectOrderId: params.projectOrderId,
    };
    if (params.parentId === 0) {
      delete option.parentId;
    }

    const boardSlug = params.boardSlug;
    return new Promise((resolve, reject) => {
      HttpRequest.put(`/api/v1/approval/document/${boardSlug}`, option)
        .then((response) => {
          commit("CREATE_POST", response);
          resolve(response);
        })
        .catch((err) => {
          commit("ERROR_CREATE_POST", err);
          reject(err);
          return err;
        });
    });
  },

  async DELETE_POST({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/approval/${boardSlug}/${postId}`)
        .then((response) => {
          commit("DELETE_POST", response);
          resolve(response);
        })
        .catch((err) => {
          commit("ERROR_DELETE_POST", err);
          reject(err);
          return err;
        });
    });
  },

  // eslint-disable-next-line no-unused-vars
  async UNTEMP_POST({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/approval/${boardSlug}/${postId}/untemp`)
        .then((response) => {
          commit("UNTEMP_POST", response);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  // eslint-disable-next-line no-unused-vars
  async UPDATE_POST({ commit }, params) {
    const option = {
      title: params.title, // 게시글 제목
      content: params.content, // 게시글 내용
      approvalLine: params.approvalLine, // 결재선 정의
      documentCompanyCodes: params.documentCompanyCodes, // 처리부서 정보
      uploads: params.uploads,
      unTemp: params.unTemp, // 임시저장 해제 여부
      projectId: params.projectId,
      projectOrderId: params.projectOrderId,
    };

    const boardSlug = params.boardSlug;
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/approval/${boardSlug}/${postId}/modify`, option)
        .then((response) => {
          commit("UPDATE_POST", response);
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  async LOAD_LINE_TEMPLATE({ commit }, params) {
    const option = {
      params: {
        page: params.page,
        perPage: params.perPage,
        keyword: params.keyword,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get("/api/v1/approval/line/template", option)
        .then((response) => {
          commit("LOAD_LINE_TEMPLATE", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async CREATE_LINE_TEMPLATE({ commit }, params) {
    const option = {
      title: params.title, // 결재선라인 제목
      desc: params.desc, // 설명
      approvalLine: params.approvalLine, // 결재선 정의
      documentCompanyCodes: params.documentCompanyCodes, // 처리부서 정보
      type: params.type, // {personal(개인, default), shared(공유)}
    };

    return new Promise((resolve, reject) => {
      HttpRequest.put("/api/v1/approval/line/template", option)
        .then((response) => {
          commit("CREATE_LINE_TEMPLATE", response);
          resolve(response);
        })
        .catch((err) => {
          commit("ERROR_CREATE_POST", err);
          reject(err);
          return err;
        });
    });
  },

  async DELETE_LINE_TEMPLATE({ commit }, params) {
    const templateId = params.templateId;
    return new Promise((resolve, reject) => {
      HttpRequest.delete(`/api/v1/approval/line/template/${templateId}`)
        .then((response) => {
          commit("DELETE_LINE_TEMPLATE", response);
          resolve(response);
        })
        .catch((err) => {
          commit("ERROR_CREATE_POST", err);
          reject(err);
          return err;
        });
    });
  },

  async ADD_AFTER_DATA({ commit }, params) {
    const option = {
      content: params.content,
    };
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/approval/${boardSlug}/${postId}/after`, option)
        .then((response) => {
          commit("ADD_AFTER_DATA", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async UPDATE_AFTER_DATA({ commit }, params) {
    const option = {
      content: params.content,
    };
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const afterId = params.afterId;
    return new Promise((resolve, reject) => {
      HttpRequest.put(`/api/v1/approval/${boardSlug}/${postId}/after/${afterId}`, option)
        .then((response) => {
          commit("UPDATE_AFTER_DATA", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async LOAD_APPROVAL_STATUS({ commit }, params) {
    const option = {
      params: {
        projectId: params.projectId,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get("/api/v1/approval/status", option)
        .then((response) => {
          commit("LOAD_APPROVAL_STATUS", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async LOAD_DISASTER_TYPE_LIST({ commit }) {
    const disasterType = {
      data: [
        { id: 1, name: "감염" },
        { id: 2, name: "감전" },
        { id: 3, name: "교통사고" },
        { id: 4, name: "깔림" },
        { id: 5, name: "끼임" },
        { id: 6, name: "낙하" },
        { id: 7, name: "넘어짐" },
        { id: 8, name: "뒤집힘" },
        { id: 9, name: "떨어짐" },
        { id: 10, name: "맞음" },
        { id: 11, name: "무너짐" },
        { id: 12, name: "근골격계질환" },
        { id: 14, name: "베임" },
        { id: 15, name: "붕괴" },
        { id: 16, name: "빠짐" },
        { id: 17, name: "(산소)결핍" },
        { id: 18, name: "소음노출" },
        { id: 19, name: "아차사고" },
        { id: 20, name: "이상기압 노출, 접촉" },
        { id: 21, name: "익사" },
        { id: 22, name: "유해광선 노출" },
        { id: 23, name: "절단" },
        { id: 24, name: "중독" },
        { id: 25, name: "질식" },
        { id: 26, name: "질환" },
        { id: 27, name: "찔림" },
        { id: 28, name: "충돌" },
        { id: 29, name: "탈락" },
        { id: 30, name: "파열" },
        { id: 31, name: "폭발" },
        { id: 32, name: "화재" },
        { id: 33, name: "화학물질 누출 접촉" },
        { id: 34, name: "기타" },
      ],
    };

    commit("LOAD_DISASTER_TYPE_LIST", disasterType);
  },

  // 알림
  async LOAD_NOTIFICATION_UNREAD_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_NOTIFICATION_UNREAD_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            type: "unread", // all, read, unread
            page_unread: params.page_unread,
            per_page_unread: params.per_page_unread,
            days_before_unread: params.days_before_unread, // 1, 7 , 30
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/notification", option)
            .then((response) => {
              if (response.unread) {
                // 데이터를 불러온 시각을 추가한다
                response.unread["loaded_at"] = new Date();
              }
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_NOTIFICATION_UNREAD_LIST",
      60000
    ); // 1분(60000ms) 동안 캐싱
  },

  async LOAD_NOTIFICATION_LIST({ commit }, params) {
    const cacheKey = getCacheKey("LOAD_NOTIFICATION_LIST", params);

    return cachedApiCall(
      cacheKey,
      () => {
        const option = {
          params: {
            type: params.type, // all, read, unread

            // all
            page: params.page,
            per_page: params.per_page,
            days_before: params.days_before, // 1, 7 , 30

            // read
            page_read: params.page_read,
            per_page_read: params.per_page_read,
            days_before_read: params.days_before_read, // 1, 7 , 30

            // unread
            page_unread: params.page_unread,
            per_page_unread: params.per_page_unread,
            days_before_unread: params.days_before_unread, // 1, 7 , 30
          },
        };

        return new Promise((resolve, reject) => {
          HttpRequest.get("/api/v1/notification", option)
            .then((response) => {
              if (response.unread) {
                // 데이터를 불러온 시각을 추가한다
                response.unread["loaded_at"] = new Date();
              }

              resolve(response);
            })
            .catch((err) => reject(err));
        });
      },
      commit,
      "LOAD_NOTIFICATION_LIST",
      30000
    ); // 30초(30000ms) 동안 캐싱
  },

  async READ_NOTIFICATION({ commit }, params) {
    const option = {
      ids: params.ids,
    };
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/notification/read`, option)
        .then((response) => {
          commit("READ_NOTIFICATION", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async DELETE_NOTIFICATION({ commit }, params) {
    const option = {
      ids: params.ids,
    };
    return new Promise((resolve, reject) => {
      HttpRequest.post(`/api/v1/notification/delete`, option)
        .then((response) => {
          commit("DELETE_NOTIFICATION", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  // 결재선 지정을 위한 회원 목록 조회
  async LOAD_FORLINE_LIST({ commit }, params) {
    const option = {
      params: {
        page: params.page,
        perPage: params.perPage,
        keyword: params.keyword,
        projectId: params.projectId,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get("/api/v1/member/forLine", option)
        .then((response) => {
          commit("LOAD_FORLINE_LIST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
};
